"use client";

import { useState, useTransition } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { processPayment } from "@/app/actions/payment";
import { CheckCircle, CreditCard, Lock } from "lucide-react";

interface FakeStripeCheckoutProps {
  planId: number;
  planTitle: string;
  planPrice: number;
  onCancel: () => void;
}

export function FakeStripeCheckout({ planId, planTitle, planPrice, onCancel }: FakeStripeCheckoutProps) {
  const [isPending, startTransition] = useTransition();
  const [step, setStep] = useState<"payment" | "processing" | "success">("payment");
  const router = useRouter();

  const handlePayment = () => {
    setStep("processing");
    startTransition(async () => {
      const result = await processPayment(planId);

      if (result.success) {
        setStep("success");
        setTimeout(() => {
          if (result.redirectUrl) {
            // Dispatch event to refresh dashboard if it's open
            window.dispatchEvent(new CustomEvent('dashboard:refresh'));
            router.push(result.redirectUrl);
          }
        }, 2000);
      } else {
        alert(result.error || "Payment failed");
        setStep("payment");
      }
    });
  };

  if (step === "processing") {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <CardTitle>Processing Payment...</CardTitle>
          <CardDescription>Please wait while we process your payment</CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  if (step === "success") {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle className="h-16 w-16 text-green-500" />
          </div>
          <CardTitle className="text-green-600">Payment Successful!</CardTitle>
          <CardDescription>Your plan has been upgraded successfully</CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-sm text-muted-foreground">
            Redirecting to your profile...
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lock className="h-5 w-5" />
          Secure Checkout (Demo)
        </CardTitle>
        <CardDescription>
          Complete your upgrade to {planTitle} - ${planPrice}/month
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-blue-50 p-3 rounded-lg">
          <p className="text-sm text-blue-700 font-medium">
            🧪 Demo Mode: This is a fake payment for testing purposes
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" defaultValue="demo@example.com" disabled />
          </div>

          <div>
            <Label htmlFor="card" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Card Information
            </Label>
            <Input id="card" defaultValue="4242 4242 4242 4242" disabled />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="expiry">Expiry</Label>
              <Input id="expiry" defaultValue="12/25" disabled />
            </div>
            <div>
              <Label htmlFor="cvc">CVC</Label>
              <Input id="cvc" defaultValue="123" disabled />
            </div>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex justify-between items-center mb-4">
            <span className="font-medium">Total</span>
            <span className="font-bold text-lg">${planPrice}/month</span>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} className="flex-1">
              Cancel
            </Button>
            <Button
              onClick={handlePayment}
              disabled={isPending}
              className="flex-1"
            >
              {isPending ? "Processing..." : `Pay $${planPrice}`}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}